import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';

export interface ScanVraag {
  id: string;
  type: 'STERREN' | 'OPEN';
  tekst: string;
  label_laag?: string | null;
  label_hoog?: string | null;
}

export type ScanAntwoorden = Record<string, number | string>;

/** Alle sterrenvragen beantwoord? Open vragen zijn optioneel. */
export const scanCompleet = (vragen: ScanVraag[], a: ScanAntwoorden) => vragen.every((v) => v.type === 'OPEN' || typeof a[v.id] === 'number');

/**
 * De vragen van een Bedrijfsthermometer-campagne: 1–5 sterren (met
 * schaaluitleg bij 1 en 5) of een open tekstveld. Gedeeld door het
 * medewerkersportaal en de publieke scanlink.
 */
export const ScanVragenlijst: React.FC<{ vragen: ScanVraag[]; antwoorden: ScanAntwoorden; zet: (id: string, waarde: number | string) => void }> = ({ vragen, antwoorden, zet }) => {
  const { t } = useTranslation('medewerker');
  return (
    <>
      {vragen.map((v, i) => (
        <fieldset key={v.id}>
          <legend className="font-medium text-gray-900">{i + 1}. {v.tekst}</legend>
          {v.type === 'STERREN' ? (
            <>
              <div className="mt-2 flex gap-1" role="radiogroup" aria-label={v.tekst}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const actief = typeof antwoorden[v.id] === 'number' && (antwoorden[v.id] as number) >= n;
                  return (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={antwoorden[v.id] === n}
                      aria-label={t('peiling.sterren', { defaultValue: '{{n}} van 5 sterren', n })}
                      onClick={() => zet(v.id, n)}
                      className="rounded-lg p-1.5 hover:bg-amber-50"
                    >
                      <Star className={`h-8 w-8 ${actief ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                    </button>
                  );
                })}
              </div>
              {(v.label_laag || v.label_hoog) && (
                <p className="mt-1 flex justify-between gap-4 text-xs text-gray-500">
                  <span>1 = {v.label_laag ?? '—'}</span>
                  <span className="text-right">5 = {v.label_hoog ?? '—'}</span>
                </p>
              )}
            </>
          ) : (
            <>
              <textarea
                rows={3}
                maxLength={2000}
                className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm"
                placeholder={t('peiling.openPlaceholder', { defaultValue: 'Optioneel — noem geen namen.' })}
                value={(antwoorden[v.id] as string) ?? ''}
                onChange={(e) => zet(v.id, e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">{t('peiling.sanitizer', { defaultValue: 'Namen, afdelingen en contactgegevens worden automatisch uit je tekst verwijderd.' })}</p>
            </>
          )}
        </fieldset>
      ))}
    </>
  );
};

export default ScanVragenlijst;
